<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MetaLeadFormsAPI from 'dashboard/api/metaLeadForms';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import FormConfig from './FormConfig.vue';
import LeadsPanel from './LeadsPanel.vue';

const store = useStore();
const { t } = useI18n();
const PERIODS = ['7', '30', '90'];

const loading = ref(true);
const forms = ref([]);
const metrics = ref([]);
const loadingMetrics = ref(false);
const period = ref('30');
const formRef = ref(null);

const inboxesList = computed(() => store.getters['inboxes/getInboxes'] || []);

const inboxName = id => {
  const inbox = inboxesList.value.find(i => i.id === id);
  return inbox?.name || t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.INBOX_FALLBACK', { id });
};

const aiOn = form =>
  form?.capture_config?.ai_continue === true ||
  form?.capture_config?.ai_continue === 'true';

const mappingCount = form => {
  const fm = form?.capture_config?.field_mapping;
  return fm && typeof fm === 'object' ? Object.keys(fm).length : 0;
};

// Falha de carga vira aviso com "Tentar de novo": zerar a lista em silêncio
// fazia a tela dizer que não havia formulários nem leads.
const formsError = ref(false);
const metricsError = ref(false);

const loadForms = async () => {
  try {
    const { data } = await MetaLeadFormsAPI.get();
    forms.value = Array.isArray(data) ? data : [];
    formsError.value = false;
  } catch (e) {
    formsError.value = true;
  }
};

const loadMetrics = async () => {
  loadingMetrics.value = true;
  try {
    const since = new Date(
      Date.now() - Number(period.value) * 24 * 60 * 60 * 1000
    ).toISOString();
    const { data } = await MetaLeadFormsAPI.metrics({ since });
    metrics.value = Array.isArray(data?.by_form) ? data.by_form : [];
    metricsError.value = false;
  } catch (e) {
    metrics.value = [];
    metricsError.value = true;
  } finally {
    loadingMetrics.value = false;
  }
};

const loadAll = async () => {
  loading.value = true;
  try {
    if (!inboxesList.value.length) await store.dispatch('inboxes/get');
    await Promise.all([loadForms(), loadMetrics()]);
  } finally {
    loading.value = false;
  }
};
onMounted(loadAll);

const formName = form =>
  forms.value.find(f => f.capture_config?.meta_form_id === form.form_id)?.name ||
  form.form_id ||
  t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.EMPTY_VALUE');

const formLabel = f => f.name || t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.FORM_FALLBACK');

const formSummary = f =>
  [
    inboxName(f.inbox_id),
    aiOn(f) ? t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.AI_ON') : t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.AI_OFF'),
    t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.FIELDS_COUNT', { count: mappingCount(f) }),
    f.capture_config?.meta_form_id
      ? t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.FORM_ID', { id: f.capture_config.meta_form_id })
      : t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.ANY_FORM'),
  ].join(' · ');

const newForm = () => formRef.value?.open(null);
const editForm = f => formRef.value?.open(f);

// Excluir pelo Dialog do design system (antes era window.confirm).
const deleteDialogRef = ref(null);
const formToDelete = ref(null);
const deleting = ref(false);

const deleteForm = f => {
  formToDelete.value = f;
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  const f = formToDelete.value;
  if (!f || deleting.value) return;
  deleting.value = true;
  try {
    await MetaLeadFormsAPI.delete(f.id);
    deleteDialogRef.value?.close();
    await Promise.all([loadForms(), loadMetrics()]);
  } catch (e) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.DELETE_ERROR'));
  } finally {
    deleting.value = false;
  }
};

const onSaved = async () => {
  await Promise.all([loadForms(), loadMetrics()]);
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto">
    <div class="w-full max-w-[60rem] mx-auto p-6 flex flex-col gap-6">
      <header class="flex flex-col gap-1">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.DESCRIPTION') }}
        </p>
      </header>

      <div v-if="loading" class="flex justify-center py-10 text-n-slate-11">
        <Spinner />
      </div>

      <template v-else>
        <!-- A configuração do app Meta (webhooks + app secret + verify token) é a
             MESMA dos comentários IG/FB, fica num lugar só, em Integrações > Meta. -->
        <p
          class="text-xs text-n-slate-11 p-3 rounded-xl bg-n-alpha-2 border border-n-weak"
        >
          <span class="i-ri-information-line size-3.5 align-text-bottom" />
          {{
            $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.APP_NOTICE', {
              field: 'leadgen',
              path: $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.APP_NOTICE_PATH'),
            })
          }}
        </p>

        <!-- Formulários configurados -->
        <section class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.CONFIGURED') }}
            </h2>
            <Button
              :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.NEW')"
              size="sm"
              icon="i-ri-add-line"
              @click="newForm"
            />
          </div>
          <div
            v-if="formsError"
            data-testid="forms-error"
            role="alert"
            class="flex items-center justify-between gap-3 p-3 rounded-xl border border-n-weak text-xs text-n-slate-11"
          >
            {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LOAD_ERROR') }}
            <Button
              :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.RETRY')"
              variant="faded"
              color="slate"
              size="xs"
              @click="loadForms"
            />
          </div>
          <div v-else-if="forms.length" class="flex flex-col gap-2">
            <div
              v-for="f in forms"
              :key="f.id"
              class="flex items-center justify-between gap-3 p-3 rounded-xl border border-n-weak"
            >
              <div class="flex flex-col gap-0.5 min-w-0">
                <span class="text-sm font-medium text-n-slate-12 truncate">
                  {{ formLabel(f) }}
                </span>
                <span class="text-xs text-n-slate-11 truncate">
                  {{ formSummary(f) }}
                </span>
              </div>
              <div class="flex items-center gap-1 shrink-0">
                <Button
                  variant="faded"
                  color="slate"
                  size="sm"
                  icon="i-ri-pencil-line"
                  :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.EDIT', { name: formLabel(f) })"
                  @click="editForm(f)"
                />
                <Button
                  variant="faded"
                  color="ruby"
                  size="sm"
                  icon="i-ri-delete-bin-line"
                  :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.DELETE', { name: formLabel(f) })"
                  @click="deleteForm(f)"
                />
              </div>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.EMPTY') }}
          </p>
        </section>

        <!-- Contatos recebidos (dashboard por formulário) -->
        <LeadsPanel :forms="forms" />

        <!-- Métricas geral (todos os formulários) -->
        <section class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h2 class="text-sm font-semibold text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.METRICS') }}
            </h2>
            <select
              v-model="period"
              class="w-40 shrink-0 px-2 py-1 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-xs"
              :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.PERIOD')"
              @change="loadMetrics"
            >
              <option v-for="days in PERIODS" :key="days" :value="days">
                {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LAST_DAYS', { days }) }}
              </option>
            </select>
          </div>
          <div
            v-if="loadingMetrics"
            class="flex justify-center py-4 text-n-slate-11"
          >
            <Spinner />
          </div>
          <div
            v-else-if="metricsError"
            data-testid="metrics-error"
            role="alert"
            class="flex items-center justify-between gap-3 p-3 rounded-xl border border-n-weak text-xs text-n-slate-11"
          >
            {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.METRICS_ERROR') }}
            <Button
              :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.RETRY')"
              variant="faded"
              color="slate"
              size="xs"
              @click="loadMetrics"
            />
          </div>
          <div
            v-else-if="metrics.length"
            class="rounded-xl border border-n-weak overflow-hidden"
          >
            <table class="w-full text-sm">
              <thead class="bg-n-alpha-1 text-n-slate-11">
                <tr>
                  <th class="text-left font-medium px-3 py-2">
                    {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.COLUMN_FORM') }}
                  </th>
                  <th class="text-right font-medium px-3 py-2">
                    {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.COLUMN_RECEIVED') }}
                  </th>
                  <th class="text-right font-medium px-3 py-2">
                    {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.COLUMN_REPLIED') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="m in metrics"
                  :key="m.form_id"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2 text-n-slate-12">{{ formName(m) }}</td>
                  <td class="px-3 py-2 text-right text-n-slate-12">
                    {{ m.recebidos }}
                  </td>
                  <td class="px-3 py-2 text-right text-n-slate-12">
                    {{ m.responderam }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.METRICS_EMPTY') }}
          </p>
        </section>
      </template>
    </div>

    <FormConfig
      ref="formRef"
      :inboxes="inboxesList"
      :forms="forms"
      @saved="onSaved"
    />
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      data-testid="meta-form-delete-dialog"
      :title="
        $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.DELETE_TITLE', {
          name: formToDelete ? formLabel(formToDelete) : '',
        })
      "
      :description="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.DELETE_DESCRIPTION')"
      :confirm-button-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.DELETE_CONFIRM')"
      :is-loading="deleting"
      @confirm="confirmDelete"
      @close="formToDelete = null"
    />
  </div>
</template>
